import { useEffect, useMemo, useRef, useState } from "react";
import {
  Form,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useRefresh,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, X } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { AutocompleteCompanyInput } from "@/components/atomic-crm/companies/AutocompleteCompanyInput";
import {
  FormGuardProvider,
  useGuardedDialogClose,
} from "@/components/admin/form-guard";
import { DialogFormSubmitButton } from "@/components/admin/form-guard/DialogFormSubmitButton";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Company, Contact } from "@/components/atomic-crm/types";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import {
  getContactEmail,
  getContactFullName,
} from "@/modules/clients/clientShowUtils";
import {
  buildContactCreateDefaultsFromTicket,
  contactMatchesId,
  resolveRequesterFromContactAndCompany,
} from "@/modules/tickets/ticketRequester";
import {
  findContactsByExactEmail,
  normalizeTicketEmail,
} from "@/modules/tickets/ticketContactMatch";
import { TicketClientSummaryCard } from "@/modules/tickets/TicketClientSummaryCard";
import { TicketProjectInput } from "@/modules/tickets/TicketProjectInput";
import {
  TicketContactSearchInput,
  TicketSubjectInput,
} from "@/modules/tickets/TicketFormFields";
import { ticketDealIdOrNull } from "@/modules/tickets/ticketMergeCompare";
import {
  TicketStatusChangeDialog,
  type TicketStatusChangeRequest,
} from "@/modules/tickets/TicketStatusChangeDialog";
import { requiresTicketStatusNote } from "@/modules/tickets/ticketStatusWorkflow";
import { useTicketStatusChoices } from "@/modules/settings/tickets/useTicketWorkspaceSettings";
import { ContactFormDialog } from "@/modules/contacts/ContactFormDialog";
import { CONTACT_STATUS_FILTER } from "@/modules/shared/relatedFilters";
import type { Ticket } from "@/modules/types";
import { TicketClientSuggestions } from "@/modules/tickets/TicketClientSuggestions";
import { patchTicketInQueryCache } from "@/modules/tickets/ticketsRealtimeCache";
import { CreateFormFieldRow } from "@/modules/shared/createForm/CreateFormLayout";
import { cn } from "@/lib/utils";
import { useOrgModules } from "@/modules/useOrgModules";
import { useIsMobile } from "@/hooks/use-mobile";

const EDIT_TICKET_FORM_ID = "lbs-edit-ticket-form";

const priorityChoices = [
  { id: "low", name: "Low" },
  { id: "normal", name: "Normal" },
  { id: "high", name: "High" },
  { id: "urgent", name: "Urgent" },
];

type EditTicketFormValues = {
  subject: string;
  priority: string;
  status: string;
  company_id: Identifier | null;
  contact_id: Identifier | null;
  deal_id: Identifier | null;
  requester_email: string;
  requester_name: string;
};

type EditTicketDialogProps = {
  ticket: Ticket | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

export const EditTicketDialog = ({
  ticket,
  open,
  onOpenChange,
  onSaved,
}: EditTicketDialogProps) => {
  const isMobile = useIsMobile();
  const [isSaving, setIsSaving] = useState(false);
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const [update] = useUpdate();
  const [statusChangeRequest, setStatusChangeRequest] =
    useState<TicketStatusChangeRequest | null>(null);
  const pendingFormSaveRef = useRef<EditTicketFormValues | null>(null);

  const defaultValues = useMemo(
    (): EditTicketFormValues => ({
      subject: ticket?.subject?.trim() ?? "",
      priority: ticket?.priority?.trim() || "normal",
      status: ticket?.status?.trim() || "open",
      company_id: ticket?.company_id ?? null,
      contact_id: ticket?.contact_id ?? null,
      deal_id: ticket?.deal_id ?? null,
      requester_email: ticket?.requester_email?.trim() ?? "",
      requester_name: ticket?.requester_name?.trim() ?? "",
    }),
    [ticket, open],
  );

  const saveTicketFields = async (values: EditTicketFormValues) => {
    if (!ticket) return;

    let contact: Contact | null = null;
    let company: Company | null = null;

    if (values.contact_id != null) {
      const { data } = await dataProvider.getOne<Contact>("contacts", {
        id: values.contact_id,
      });
      contact = data;
    }

    if (values.company_id != null) {
      const { data } = await dataProvider.getOne<Company>("companies", {
        id: values.company_id,
      });
      company = data;
    }

    const resolved = resolveRequesterFromContactAndCompany(contact, company);
    const requesterEmail =
      values.requester_email.trim() || resolved.email || null;
    const requesterName = values.requester_name.trim() || resolved.name || null;

    await update(
      "tickets",
      {
        id: ticket.id,
        data: {
          subject: values.subject.trim(),
          priority: values.priority || "normal",
          status: values.status || "open",
          company_id: values.company_id,
          contact_id: values.contact_id,
          deal_id: ticketDealIdOrNull(values.deal_id),
          requester_email: requesterEmail,
          requester_name: requesterName,
        },
        previousData: ticket,
      },
      { returnPromise: true },
    );

    patchTicketInQueryCache(queryClient, ticket.id, {
      subject: values.subject.trim(),
      priority: values.priority || "normal",
      status: values.status || "open",
      company_id: values.company_id,
      contact_id: values.contact_id,
      deal_id: ticketDealIdOrNull(values.deal_id),
      requester_email: requesterEmail,
      requester_name: requesterName,
    });
    refresh();
    onOpenChange(false);
    onSaved?.();
    notify("Ticket updated", { type: "success" });
  };

  const handleSubmit = async (values: EditTicketFormValues) => {
    if (!ticket) return;

    const subject = values.subject.trim();
    if (!subject) {
      notify("Subject is required", { type: "warning" });
      return;
    }

    const previousStatus = ticket.status?.trim() || "open";
    const nextStatus = values.status?.trim() || "open";

    if (
      nextStatus !== previousStatus &&
      requiresTicketStatusNote(previousStatus, nextStatus)
    ) {
      pendingFormSaveRef.current = { ...values, subject };
      setStatusChangeRequest({ ticket, nextStatus });
      return;
    }

    setIsSaving(true);
    try {
      await saveTicketFields({ ...values, subject });
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Failed to update ticket",
        { type: "error" },
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!open || !ticket) return null;

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          className={cn(
            "flex max-h-[min(92vh,44rem)] w-full max-w-[calc(100%-1rem)] flex-col gap-0 overflow-hidden p-0",
            "sm:max-w-2xl",
            isMobile &&
              "top-auto bottom-0 left-1/2 max-h-[92vh] translate-x-[-50%] translate-y-0 rounded-b-none rounded-t-2xl",
          )}
        >
          <Form
            id={EDIT_TICKET_FORM_ID}
            key={ticket.id}
            className="flex min-h-0 flex-1 flex-col"
            defaultValues={defaultValues}
            onSubmit={(values: Record<string, unknown>) =>
              handleSubmit(values as EditTicketFormValues)
            }
          >
            <FormGuardProvider draftKey="edit-ticket" enabled={false}>
              <EditTicketDialogBody
                ticket={ticket}
                isMobile={isMobile}
                isSaving={isSaving}
                onOpenChange={onOpenChange}
              />
            </FormGuardProvider>
          </Form>
        </DialogContent>
      </Dialog>

      <TicketStatusChangeDialog
        request={statusChangeRequest}
        onClose={() => {
          setStatusChangeRequest(null);
          pendingFormSaveRef.current = null;
        }}
        onSuccess={() => {
          void (async () => {
            const pending = pendingFormSaveRef.current;
            pendingFormSaveRef.current = null;
            if (!pending) return;
            setIsSaving(true);
            try {
              await saveTicketFields(pending);
            } catch (error) {
              notify(
                error instanceof Error
                  ? error.message
                  : "Failed to update ticket",
                { type: "error" },
              );
            } finally {
              setIsSaving(false);
            }
          })();
        }}
      />
    </>
  );
};

const EditTicketDialogBody = ({
  ticket,
  isMobile,
  isSaving,
  onOpenChange,
}: {
  ticket: Ticket;
  isMobile: boolean;
  isSaving: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const guardedClose = useGuardedDialogClose(onOpenChange);
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const { setValue } = useFormContext<EditTicketFormValues>();
  const statusChoices = useTicketStatusChoices();
  const [createContactOpen, setCreateContactOpen] = useState(false);
  // Accounts → People only: tickets belong to a person, not a company.
  const peopleOnly = useOrgModules().option("accounts", "peopleOnly");
  const companyId = useWatch({ name: "company_id" });
  const contactId = useWatch({ name: "contact_id" });
  const requesterEmail = useWatch({ name: "requester_email" });
  const requesterName = useWatch({ name: "requester_name" });

  const normalizedRequesterEmail = normalizeTicketEmail(
    requesterEmail || ticket.requester_email,
  );

  const { data: linkedContact } = useGetOne<Contact>(
    "contacts",
    { id: ticket.contact_id ?? "" },
    { enabled: Boolean(ticket.contact_id) },
  );

  const shouldSearchContactByEmail =
    !ticket.contact_id && normalizedRequesterEmail.length > 0;

  const { data: emailSearchResults = [] } = useGetList<Contact>(
    "contacts",
    {
      filter: { q: normalizedRequesterEmail },
      pagination: { page: 1, perPage: 25 },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: shouldSearchContactByEmail, staleTime: 30_000 },
  );

  const emailMatchedContacts = useMemo(
    () =>
      findContactsByExactEmail(emailSearchResults, normalizedRequesterEmail),
    [emailSearchResults, normalizedRequesterEmail],
  );

  const autoMatchedContact =
    linkedContact ??
    (emailMatchedContacts.length === 1 ? emailMatchedContacts[0] : null);

  const autoLinkedRef = useRef(false);

  useEffect(() => {
    if (!autoMatchedContact || autoLinkedRef.current) return;

    if (autoMatchedContact.company_id != null) {
      setValue("company_id", autoMatchedContact.company_id, {
        shouldDirty: false,
      });
    }
    setValue("contact_id", autoMatchedContact.id, { shouldDirty: false });

    const resolved = resolveRequesterFromContactAndCompany(
      autoMatchedContact,
      null,
    );
    if (resolved.email) {
      setValue("requester_email", resolved.email, { shouldDirty: false });
    }
    if (resolved.name) {
      setValue("requester_name", resolved.name, { shouldDirty: false });
    }

    autoLinkedRef.current = true;
  }, [autoMatchedContact, setValue]);

  const { data: companyContacts = [] } = useGetList<Contact>(
    "contacts",
    {
      filter: {
        "company_id@eq": companyId,
        "status@in": CONTACT_STATUS_FILTER,
      },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: Boolean(companyId), staleTime: 30_000 },
  );

  const { data: company } = useGetOne<Company>(
    "companies",
    { id: companyId ?? "" },
    { enabled: Boolean(companyId) },
  );

  const contactChoices = useMemo(
    () =>
      companyContacts.map((contact) => ({
        id: contact.id,
        name: getContactFullName(contact),
      })),
    [companyContacts],
  );

  const selectedContact = useMemo(
    () =>
      companyContacts.find((contact) => contactMatchesId(contact, contactId)),
    [companyContacts, contactId],
  );

  const activeContact =
    selectedContact ?? autoMatchedContact ?? linkedContact ?? null;
  const contactEmptyText = !companyId
    ? "Select a company first"
    : contactChoices.length === 0
      ? "No contacts for this company"
      : "Select contact";

  useEffect(() => {
    // People only: the contact is picked directly, not from a company.
    if (peopleOnly) return;
    if (!companyId) {
      setValue("contact_id", null);
      return;
    }

    if (
      contactId != null &&
      companyContacts.some((contact) => contactMatchesId(contact, contactId))
    ) {
      return;
    }

    if (companyContacts.length === 1) {
      setValue("contact_id", companyContacts[0].id);
      return;
    }

    const primaryId = company?.primary_contact_id;
    if (primaryId != null) {
      const primaryContact = companyContacts.find((contact) =>
        contactMatchesId(contact, primaryId),
      );
      if (primaryContact) {
        setValue("contact_id", primaryContact.id);
      }
    }
  }, [peopleOnly, companyId, contactId, companyContacts, company, setValue]);

  useEffect(() => {
    if (contactId && !selectedContact) return;

    const resolved = resolveRequesterFromContactAndCompany(
      selectedContact ?? null,
      company,
    );

    if (resolved.email) {
      setValue("requester_email", resolved.email, { shouldDirty: true });
    }
    if (resolved.name) {
      setValue("requester_name", resolved.name, { shouldDirty: true });
    }
  }, [contactId, selectedContact, company, setValue]);

  const canCreateContactFromTicket =
    !selectedContact &&
    !autoMatchedContact &&
    emailMatchedContacts.length === 0 &&
    Boolean(requesterEmail?.trim() || requesterName?.trim());

  const contactCreateDefaults = useMemo(
    () =>
      buildContactCreateDefaultsFromTicket(
        ticket,
        companyId ?? ticket.company_id,
      ),
    [ticket, companyId],
  );

  const linkContactToTicket = async (contact: Contact) => {
    const resolved = resolveRequesterFromContactAndCompany(contact, company);
    setValue("contact_id", contact.id, { shouldDirty: true });
    if (contact.company_id != null) {
      setValue("company_id", contact.company_id, { shouldDirty: true });
    }
    if (resolved.email) {
      setValue("requester_email", resolved.email, { shouldDirty: true });
    }
    if (resolved.name) {
      setValue("requester_name", resolved.name, { shouldDirty: true });
    }

    await update(
      "tickets",
      {
        id: ticket.id,
        data: {
          contact_id: contact.id,
          company_id:
            contact.company_id ?? companyId ?? ticket.company_id ?? null,
          requester_email:
            resolved.email ||
            getContactEmail(contact) ||
            ticket.requester_email,
          requester_name:
            resolved.name ||
            getContactFullName(contact) ||
            ticket.requester_name,
        },
        previousData: ticket,
      },
      { returnPromise: true },
    );
    refresh();
    notify("Contact created and linked to this ticket", { type: "success" });
  };

  return (
    <>
      <DialogHeader className="relative shrink-0 space-y-1 border-b bg-background px-5 py-4 pr-12 text-left sm:px-6 sm:pr-14">
        <DialogTitle>Edit ticket #{ticket.id}</DialogTitle>
        <DialogDescription>
          Update the subject, client, or contact. Replies are sent to the
          selected contact&apos;s email automatically.
        </DialogDescription>
        <DialogClose
          className="absolute top-3.5 right-3.5 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
          disabled={isSaving}
          onClick={(event) => {
            event.preventDefault();
            guardedClose(false);
          }}
        >
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 sm:px-6">
        <div className="flex flex-col gap-4">
          <TicketSubjectInput />

          <CreateFormFieldRow>
            <SelectInput
              source="priority"
              label="Priority"
              choices={priorityChoices}
              helperText={false}
              labelVariant="floating"
            />
            <SelectInput
              source="status"
              label="Status"
              choices={statusChoices}
              helperText={false}
              labelVariant="floating"
            />
          </CreateFormFieldRow>

          {peopleOnly ? (
            <TicketContactSearchInput />
          ) : (
            <CreateFormFieldRow>
              <ReferenceInput source="company_id" reference="companies">
                <AutocompleteCompanyInput
                  label="Account"
                  labelVariant="floating"
                />
              </ReferenceInput>
              <SelectInput
                source="contact_id"
                label="Contact"
                choices={contactChoices}
                optionText="name"
                optionValue="id"
                helperText={false}
                disabled={!companyId}
                emptyText={contactEmptyText}
                labelVariant="floating"
              />
            </CreateFormFieldRow>
          )}

          <TicketProjectInput companyId={companyId} />

          {activeContact || company ? (
            <TicketClientSummaryCard
              ticket={ticket}
              company={company}
              contact={activeContact}
              matchedFromEmail={
                !ticket.contact_id && Boolean(autoMatchedContact)
              }
            />
          ) : null}

          {!activeContact && !company ? (
            <div className="space-y-3 rounded-lg border border-dashed px-3 py-3">
              <TicketClientSuggestions ticket={ticket} />
              {emailMatchedContacts.length > 1 ? (
                <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2.5">
                  <p className="text-sm text-foreground">
                    Multiple contacts use {normalizedRequesterEmail}. Pick the
                    right one in Contact above.
                  </p>
                </div>
              ) : null}
              {canCreateContactFromTicket ? (
                <div className="rounded-none border border-border bg-muted/30 px-3 py-2.5">
                  <p className="text-sm text-foreground">
                    This sender is not saved as a contact yet.
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Create a contact from the name and email on this ticket. You
                    can finish company details in the same form.
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-2"
                    onClick={() => setCreateContactOpen(true)}
                  >
                    <Plus className="size-4" />
                    Create contact from sender
                  </Button>
                </div>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {contactId
                  ? "This contact has no email on file. Enter who should receive replies."
                  : "Select a contact above, or enter who should receive replies manually."}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextInput
                  source="requester_name"
                  label="Recipient name"
                  helperText={false}
                  labelVariant="floating"
                />
                <TextInput
                  source="requester_email"
                  label="Recipient email"
                  helperText={false}
                  labelVariant="floating"
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <DialogFooter
        className={cn(
          "shrink-0 gap-2 border-t bg-background px-5 py-4 sm:px-6",
          isMobile && "flex-col-reverse",
        )}
      >
        <Button
          type="button"
          variant="secondary"
          disabled={isSaving}
          className={cn(isMobile && "w-full")}
          onClick={() => guardedClose(false)}
        >
          Cancel
        </Button>
        <DialogFormSubmitButton
          formId={EDIT_TICKET_FORM_ID}
          disabled={isSaving}
          className={cn("rounded-full px-5", isMobile && "w-full")}
        >
          {isSaving ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Saving…
            </>
          ) : (
            "Save changes"
          )}
        </DialogFormSubmitButton>
      </DialogFooter>

      <ContactFormDialog
        open={createContactOpen}
        onOpenChange={setCreateContactOpen}
        lockCompanyId={companyId ?? ticket.company_id ?? undefined}
        navigateOnCreate={false}
        createDefaults={contactCreateDefaults}
        title="Create contact from ticket"
        description="We filled in what we already know from this ticket. Pick or add a company, then save."
        submitLabel="Create and link contact"
        onCreated={(contact) => {
          void linkContactToTicket(contact);
        }}
      />
    </>
  );
};
